/**
 * Nadine: full-screen photo viewer of the product page (markup and styles in snippets/nadine-zoom.liquid).
 * Desktop: click a photo → it fills the stage at high resolution and follows the mouse; click again → back.
 * Phones: tap → zoomed 2.5×, drag to move, pinch for more; tap again → back.
 */
const ZOOM = 2.5; // phones: zoomed width = screen width × ZOOM
const MAX_ZOOM = 5;
const TAP_SLOP = 8;

class NadineZoom extends HTMLElement {
  // Looked up on use: the gallery is morphed on colour change, so cached nodes could be stale
  get dialog() {
    return this.querySelector('dialog');
  }
  get track() {
    return this.querySelector('.nadine-zoom__track');
  }
  get slides() {
    return [...this.querySelectorAll('[data-nz-slide]')];
  }
  get thumbs() {
    return [...this.querySelectorAll('[data-nz-thumb]')];
  }
  get lens() {
    return this.querySelector('[data-nz-lens]');
  }
  get lensImage() {
    let image = this.lens.querySelector('img');
    if (!image) {
      image = new Image();
      image.alt = '';
      image.draggable = false;
      this.lens.append(image);
    }
    return image;
  }

  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    // Open: any [data-nadine-zoom] of the same gallery (grid zoom buttons, carousel slides)
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target instanceof Element && event.target.closest('[data-nadine-zoom]');
        if (!trigger || trigger.closest('media-gallery') !== this.closest('media-gallery')) return;
        event.preventDefault();
        this.open(Number(trigger.getAttribute('data-nadine-zoom')));
      },
      { signal }
    );

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.onKey(event), { signal });
    this.addEventListener('close', () => this.onClosed(), { signal, capture: true });
    this.addEventListener(
      'cancel',
      (event) => {
        // Esc leaves the zoom first, then the viewer
        if (!this.lens.hidden) {
          event.preventDefault();
          this.unzoom();
        }
      },
      { signal, capture: true }
    );
    this.addEventListener('scroll', () => this.onScroll(), { passive: true, signal, capture: true });
    this.addEventListener('mousemove', (event) => this.onMouseMove(event), { signal });
    this.addEventListener('touchstart', (event) => this.inLens(event) && this.onTouchStart(event), {
      passive: false,
      signal,
    });
    this.addEventListener('touchmove', (event) => this.inLens(event) && this.onTouchMove(event), {
      passive: false,
      signal,
    });
    this.addEventListener('touchend', (event) => this.inLens(event) && this.onTouchEnd(event), { signal });
    window.addEventListener(
      'resize',
      () => {
        if (!this.lens || this.lens.hidden) return;
        this.box = this.lens.getBoundingClientRect();
        this.render();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    document.documentElement.style.removeProperty('overflow');
  }

  inLens(event) {
    return event.target instanceof Element && !!event.target.closest('[data-nz-lens]');
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-nz-close]')) return this.close();
    const thumb = target.closest('[data-nz-thumb]');
    if (thumb) return this.goTo(Number(thumb.getAttribute('data-nz-thumb')));
    const arrow = target.closest('[data-nz-step]');
    if (arrow) return this.step(Number(arrow.getAttribute('data-nz-step')));
    if (target.closest('[data-nz-lens]')) return this.onLensClick(event);
    const slide = target.closest('[data-nz-slide]');
    if (slide) return this.zoom(slide, event);
  }

  get isPhone() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /** @param {number} mediaIndex - index of the photo in the gallery's media */
  open(mediaIndex) {
    const index = Math.max(
      0,
      this.slides.findIndex((slide) => Number(slide.dataset.mediaIndex) === mediaIndex)
    );
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    // Load the photos around the opened one right away (they are lazy until the dialog shows)
    this.goTo(index, 'instant');
    this.update();
    window.posthog?.capture?.('product_photo_zoomed', { photo: index + 1 });
  }

  close() {
    this.dialog.close();
  }

  onClosed() {
    this.unzoom();
    document.documentElement.style.removeProperty('overflow');
  }

  /** Index of the first photo in view */
  get index() {
    const slide = this.slides[0];
    if (!slide) return 0;
    const width = slide.offsetWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
    return Math.round(this.track.scrollLeft / width);
  }

  goTo(index, behavior = 'smooth') {
    const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
    if (!slide) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduced ? 'instant' : behavior });
  }

  step(direction) {
    this.goTo(this.index + direction);
  }

  onScroll() {
    if (!this.dialog?.open) return;
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  }

  update() {
    const index = this.index;
    const current = this.querySelector('[data-nz-current]');
    if (current) current.textContent = String(index + 1);
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    this.thumbs[index]?.scrollIntoView({ block: 'nearest' });
    const track = this.track;
    const prev = this.querySelector('[data-nz-step="-1"]');
    const next = this.querySelector('[data-nz-step="1"]');
    if (prev) prev.hidden = track.scrollLeft <= 2;
    if (next) next.hidden = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
  }

  onKey(event) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.unzoom();
      this.step(event.key === 'ArrowRight' ? 1 : -1);
    }
  }

  /* ---------- zoom ---------- */

  /**
   * @param {HTMLElement} slide
   * @param {MouseEvent} event - the click that zoomed (its point is kept under the finger on phones)
   */
  zoom(slide, event) {
    const shown = slide.querySelector('img');
    const ratio = parseFloat(getComputedStyle(slide).getPropertyValue('--nz-slide-ratio')) || 0.6;
    const lens = this.lens;
    lens.hidden = false;
    const box = lens.getBoundingClientRect();

    // Show the photo already on screen first (no blank lens), then swap in the 2400px file
    this.lensImage.src = shown.currentSrc || shown.src;
    const hiRes = slide.dataset.zoomSrc;
    if (hiRes && this.lensImage.dataset.src !== hiRes) {
      this.lensImage.dataset.src = hiRes;
      const loader = new Image();
      loader.onload = () => {
        if (this.lensImage.dataset.src === hiRes) this.lensImage.src = hiRes;
      };
      loader.src = hiRes;
    }

    // Base size: desktop = as wide as the stage (at least 2.5× the frame), phones = 2.5× the screen
    const frameWidth = shown.getBoundingClientRect().width || box.width / 2;
    this.baseWidth = this.isPhone ? box.width * ZOOM : Math.max(box.width, frameWidth * ZOOM);
    this.ratio = ratio;
    this.scale = 1;
    this.box = box;

    // Start where the photo was clicked
    const imageBox = shown.getBoundingClientRect();
    const rx = clamp01((event.clientX - imageBox.left) / imageBox.width);
    const ry = clamp01((event.clientY - imageBox.top) / imageBox.height);
    if (this.isPhone) {
      this.x = box.width / 2 - rx * this.width;
      this.y = box.height / 2 - ry * this.height;
    } else {
      // desktop pans by mouse position: put the clicked point where the mouse is
      this.panTo(event.clientX, event.clientY);
      return;
    }
    this.render();
  }

  unzoom() {
    this.lens.hidden = true;
    this.touch = null;
  }

  get width() {
    return this.baseWidth * this.scale;
  }

  get height() {
    return this.width / this.ratio;
  }

  /** Desktop: the mouse position across the lens is the position across the photo */
  panTo(clientX, clientY) {
    const { left, top, width, height } = this.box;
    const rx = clamp01((clientX - left) / width);
    const ry = clamp01((clientY - top) / height);
    this.x = -rx * (this.width - width);
    this.y = -ry * (this.height - height);
    this.render();
  }

  render() {
    const { width, height } = this.box;
    // Keep the photo covering the lens; centre it on an axis where it is smaller
    this.x = this.width <= width ? (width - this.width) / 2 : Math.min(0, Math.max(width - this.width, this.x));
    this.y = this.height <= height ? (height - this.height) / 2 : Math.min(0, Math.max(height - this.height, this.y));
    this.lensImage.style.width = `${this.width}px`;
    this.lensImage.style.transform = `translate3d(${this.x}px, ${this.y}px, 0)`;
  }

  onMouseMove(event) {
    if (this.lens.hidden || this.isPhone || this.touch) return;
    this.panTo(event.clientX, event.clientY);
  }

  onLensClick(event) {
    if (this.moved) {
      this.moved = false;
      return;
    }
    event.preventDefault();
    this.unzoom();
  }

  /* Phones: one finger drags, two fingers pinch */
  onTouchStart(event) {
    event.preventDefault();
    this.moved = false;
    this.touch = this.touchState(event.touches);
    this.last = { x: this.touch.x, y: this.touch.y };
  }

  onTouchMove(event) {
    event.preventDefault();
    const start = this.touch;
    if (!start) return;
    const now = this.touchState(event.touches);
    if (now.count !== start.count) {
      // a finger was added or lifted: restart from here
      this.touch = now;
      this.last = { x: now.x, y: now.y };
      return;
    }
    if (Math.hypot(now.x - start.x, now.y - start.y) > TAP_SLOP || now.count > 1) this.moved = true;

    if (now.count > 1 && start.distance) {
      // pinch around the fingers' midpoint
      const before = this.scale;
      this.scale = Math.min(MAX_ZOOM / ZOOM, Math.max(1 / ZOOM, start.scale * (now.distance / start.distance)));
      const k = this.scale / before;
      const mx = now.x - this.box.left;
      const my = now.y - this.box.top;
      this.x = mx - (mx - this.x) * k;
      this.y = my - (my - this.y) * k;
    }
    this.x += now.x - this.last.x;
    this.y += now.y - this.last.y;
    this.last = { x: now.x, y: now.y };
    this.render();
  }

  onTouchEnd(event) {
    if (event.touches.length) {
      this.touch = this.touchState(event.touches);
      this.last = { x: this.touch.x, y: this.touch.y };
      return;
    }
    const tapped = !this.moved;
    this.touch = null;
    this.last = null;
    // a tap leaves the zoom; the synthetic click that follows is swallowed by `moved`
    if (tapped) {
      event.preventDefault();
      this.unzoom();
    }
  }

  touchState(touches) {
    const list = [...touches];
    const x = list.reduce((sum, t) => sum + t.clientX, 0) / list.length;
    const y = list.reduce((sum, t) => sum + t.clientY, 0) / list.length;
    const distance =
      list.length > 1 ? Math.hypot(list[0].clientX - list[1].clientX, list[0].clientY - list[1].clientY) : 0;
    return { count: list.length, x, y, distance, scale: this.scale };
  }
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value || 0));
}

if (!customElements.get('nadine-zoom')) customElements.define('nadine-zoom', NadineZoom);
